import React, { useReducer } from 'react';
import ReactTooltip from 'react-tooltip';
import { getEventDescription, getDotColor, getFormattedTimestamp } from './ShipmentEventUtils';
import ShipmentFilterSection from './ShipmentFilterSection';
import styles from './systemEventTimelineStyles.scss';

const EVENT_TYPE_TOOLTIPS = {
  state_change: 'An update to the state, usually calculated from carrier updates (e.g., In Transit to Delivered).',
  calculated_state_change: 'Calculated by Track: indicates if the parcel may be late or missing, based on recent events.',
  custom: 'A custom event, defined by you "the customer".',
};

const initialState = {
  isMatchedExpanded: true,
  isUnmatchedExpanded: false,
  matchedDetailState: {},
  unmatchedDetailState: {},
};

const actionTypes = {
  TOGGLE_MATCHED_EXPANDED: 'TOGGLE_MATCHED_EXPANDED',
  TOGGLE_UNMATCHED_EXPANDED: 'TOGGLE_UNMATCHED_EXPANDED',
  SET_MATCHED_DETAIL_STATE: 'SET_MATCHED_DETAIL_STATE',
  SET_UNMATCHED_DETAIL_STATE: 'SET_UNMATCHED_DETAIL_STATE',
};

const rowReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.TOGGLE_MATCHED_EXPANDED:
      return { ...state, isMatchedExpanded: !state.isMatchedExpanded };
    case actionTypes.TOGGLE_UNMATCHED_EXPANDED:
      return { ...state, isUnmatchedExpanded: !state.isUnmatchedExpanded };
    case actionTypes.SET_MATCHED_DETAIL_STATE: {
      const nextMatchedDetailState = typeof action.updater === 'function'
        ? action.updater(state.matchedDetailState)
        : action.updater;
      return { ...state, matchedDetailState: nextMatchedDetailState };
    }
    case actionTypes.SET_UNMATCHED_DETAIL_STATE: {
      const nextUnmatchedDetailState = typeof action.updater === 'function'
        ? action.updater(state.unmatchedDetailState)
        : action.updater;
      return { ...state, unmatchedDetailState: nextUnmatchedDetailState };
    }
    default:
      return state;
  }
};

const ShipmentEventRow = ({
  event,
  isOpen,
  onToggle,
  onSectionToggle,
  idx,
}) => {
  const [state, baseDispatch] = useReducer(rowReducer, initialState);
  const dispatch = /** @type {import('react').Dispatch<any>} */ (baseDispatch);

  const eventType = String(event.event_type || '').toLowerCase();
  const eventDescription = getEventDescription(event);
  const eventTypeLabel = eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const eventTypeTooltip = EVENT_TYPE_TOOLTIPS[eventType] || 'Event type';
  const matchedFilters = event.matched_shipment_filters || [];
  const unmatchedFilters = event.unmatched_shipment_filters || [];

  const handleToggleSection = actionType => {
    dispatch({ type: actionType });
    if (onSectionToggle) onSectionToggle();
  };

  const setMatchedDetailState = updater => {
    dispatch({ type: actionTypes.SET_MATCHED_DETAIL_STATE, updater });
  };

  const setUnmatchedDetailState = updater => {
    dispatch({ type: actionTypes.SET_UNMATCHED_DETAIL_STATE, updater });
  };

  return (
    <li className={styles.eventItem}>
      <div
        className={styles.eventRow}
        onClick={() => onToggle(idx)}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
      >
        <div className={styles.timelineColumn}>
          <span
            className={styles.dot}
            style={{ background: getDotColor(event.event_type) }}
            title={event.event_type}
          />
        </div>
        <div className={styles.eventRowContentColumns}>
          <span className={styles.stateTransition} data-testid="state-transition">{eventDescription}</span>
          <span
            className={styles.eventType}
            data-tip
            data-for={`event-type-tooltip-${event.id || idx}`}
          >
            {eventTypeLabel}
          </span>
          <ReactTooltip
            id={`event-type-tooltip-${event.id || idx}`}
            effect="float"
            place="auto"
          >
            {eventTypeTooltip}
          </ReactTooltip>
        </div>
        <span
          className={styles.eventTimestamp}
          data-tip
          data-for={`event-timestamp-tooltip-${event.id || idx}`}
          style={{ cursor: 'help' }}
        >
          <span className={styles.eventDateLabel}>Event date: </span>{getFormattedTimestamp(event)}
        </span>

        <ReactTooltip
          id={`event-timestamp-tooltip-${event.id || idx}`}
          effect="float"
          place="auto"
        >
          The date this update was made by Track.
        </ReactTooltip>

        <span className={`${styles.chevron} ${isOpen ? styles.chevronDown : styles.chevronRight}`} />
      </div>

      {isOpen && (
        <div className={styles.dropdownContent}>
          <ShipmentFilterSection
            title="Matched Filters"
            filters={matchedFilters}
            isExpanded={state.isMatchedExpanded}
            onToggleExpanded={() => handleToggleSection(actionTypes.TOGGLE_MATCHED_EXPANDED)}
            detailState={state.matchedDetailState}
            setDetailState={setMatchedDetailState}
            onSectionToggle={onSectionToggle}
            emptyText="No matched filters"
          />

          <ShipmentFilterSection
            title="Unmatched Filters"
            filters={unmatchedFilters}
            isExpanded={state.isUnmatchedExpanded}
            onToggleExpanded={() => handleToggleSection(actionTypes.TOGGLE_UNMATCHED_EXPANDED)}
            detailState={state.unmatchedDetailState}
            setDetailState={setUnmatchedDetailState}
            onSectionToggle={onSectionToggle}
            emptyText="No unmatched filters"
          />
        </div>
      )}
    </li>
  );
};

export default ShipmentEventRow;
